import React, { useState, useRef } from 'react';
import { Button, Tooltip, IconButton } from '@mui/material';
import parse, { domToReact } from 'html-react-parser';
import {
    ComparisonSection,
    DocumentSelectionArea,
    FileInputContainer,
    FileNameDisplay,
    ButtonGroup,
    ComparisonResults,
    ComparisonTabs,
    ComparisonTab,
    DiffView,
    SideBySideView,
    SideBySideDocument,
    LineContainer,
    LineNumber,
    LineContent,
    SummaryStats,
    StatCard,
    StatValue,
    StatLabel,
    StyledButton
} from './DocumentComparisonContainer.styles';
import { Compare, UploadFile, InfoOutlined } from '@mui/icons-material';
import { documentComparisonApi } from '@api/api';

const jaccardExplanation = (
    <React.Fragment>
        Calculated using the Jaccard Index:
        <br />
        (Size of Intersection of unique words) / (Size of Union of unique words)
        <br />
        Measures similarity between word sets, ignoring frequency and order. Punctuation is ignored and text is lowercased.
        <br /><br /> {/* Add extra space before example */}
        Example: If text A is "The cat is on the mat." and text B is "The dog is on the mat.", the unique words are:
        <br />
        A: {"{"}the, cat, is, on, mat{"}"}
        <br />
        B: {"{"}the, dog, is, on, mat{"}"}
        <br />
        Intersection: {"{"}the, is, on, mat{"}"} (4 words)
        <br />
        Union: {"{"}the, cat, is, on, mat, dog{"}"} (6 words)
        <br />
        Jaccard Index = 4 / 6 ≈ 0.67 (or 67%)
    </React.Fragment>
);

export default function DocumentComparisonContainer() {
    const [file1, setFile1] = useState(null);
    const [file2, setFile2] = useState(null);
    const [comparisonResult, setComparisonResult] = useState(null);
    const [activeTab, setActiveTab] = useState('inline');
    const [comparisonMade, setComparisonMade] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // Refs for the side-by-side document containers
    const doc1Ref = useRef(null);
    const doc2Ref = useRef(null);

    const handleFile1Change = (event) => {
        const file = event.target.files[0];
        if (file) {
            setFile1(file);
            setComparisonMade(false);
            setComparisonResult(null);
            setError(null);
        }
    };

    const handleFile2Change = (event) => {
        const file = event.target.files[0];
        if (file) {
            setFile2(file);
            setComparisonMade(false);
            setComparisonResult(null);
            setError(null);
        }
    };

    const handleCompare = async () => {
        if (!file1 || !file2) {
            setError("Please select both documents to compare.");
            return;
        }
        setError(null);
        setIsLoading(true);
        setComparisonMade(false);
        setComparisonResult(null);

        try {
            const result = await documentComparisonApi.compareDocuments(file1, file2);
            setComparisonResult(result);
            setComparisonMade(true);
        } catch (err) {
            console.error("Comparison API failed:", err);
            setError(err.message || "Failed to compare documents. Please try again.");
            setComparisonResult(null);
        } finally {
            setIsLoading(false);
        }
    };

    const renderDiffContent = (diffHtml) => {
        const contentWithAdditions = diffHtml.replace(/<ins>(.*?)<\/ins>/g, `<span class="addition">\$1</span>`);
        const contentWithDeletions = contentWithAdditions.replace(/<del>(.*?)<\/del>/g, `<span class="deletion">\$1</span>`);

        return (
            <DiffView dangerouslySetInnerHTML={{ __html: contentWithDeletions.replace(/\n/g, '<br />') }} />
        );
    };

    const renderInlineDiffView = () => {
        if (!comparisonResult || !comparisonResult.inlineDiff) return <p>Inline comparison data not available.</p>;
        return renderDiffContent(comparisonResult.inlineDiff);
    };

    // --- Scroll Synchronization Handlers ---
    const handleScroll1 = () => {
        if (doc1Ref.current && doc2Ref.current) {
            const scroll1 = doc1Ref.current.scrollTop;
            const scroll2 = doc2Ref.current.scrollTop;
            // Only update if scrolls are not already in sync (prevents infinite loop)
            if (Math.abs(scroll1 - scroll2) > 1) { // Use a small tolerance
                doc2Ref.current.scrollTop = scroll1;
            }
        }
    };

    const handleScroll2 = () => {
        if (doc1Ref.current && doc2Ref.current) {
            const scroll1 = doc1Ref.current.scrollTop;
            const scroll2 = doc2Ref.current.scrollTop;
            // Only update if scrolls are not already in sync (prevents infinite loop)
             if (Math.abs(scroll2 - scroll1) > 1) { // Use a small tolerance
                doc1Ref.current.scrollTop = scroll2;
            }
        }
    };
    // --- End Scroll Synchronization Handlers ---

    const renderSideBySideView = () => {
        if (!comparisonResult || !comparisonResult.doc1ContentWithDiff || !comparisonResult.doc2ContentWithDiff) return <p>Side-by-side comparison data not available.</p>;

        // Split content into lines based on newline characters from backend
        const lines1 = comparisonResult.doc1ContentWithDiff.split('\n');
        const lines2 = comparisonResult.doc2ContentWithDiff.split('\n');
        const maxLines = Math.max(lines1.length, lines2.length);

        // Parser options for html-react-parser
        const parserOptions = {
            replace: domNode => {
                if (domNode.type === 'tag') {
                    if (domNode.name === 'ins') {
                        return <span className="addition">{domToReact(domNode.children, parserOptions)}</span>;
                    }
                    if (domNode.name === 'del') {
                        return <span className="deletion">{domToReact(domNode.children, parserOptions)}</span>;
                    }
                    // Pass through placeholder div
                    if (domNode.name === 'div' && domNode.attribs && domNode.attribs.class === 'diff-placeholder') {
                        return <div className="diff-placeholder">{domToReact(domNode.children, parserOptions)}</div>;
                    }
                    // Remove <br> tags if backend sends them - ideally backend sends only \n
                    if (domNode.name === 'br') {
                        return <></>; // Render nothing for <br>
                    }
                }
            }
        };

        // Helper to render lines for one document pane
        const renderDocumentLines = (lines, ref, scrollHandler) => (
            <SideBySideDocument ref={ref} onScroll={scrollHandler}>
                {Array.from({ length: maxLines }).map((_, index) => {
                    const lineContent = lines[index] || ''; // Get line or empty string if index out of bounds
                    // Use index + 1 for 1-based line numbering
                    const lineNumber = index + 1;
                    // Parse the content for this line
                    const parsedContent = parse(lineContent, parserOptions);

                    return (
                        <LineContainer key={index}>
                            <LineNumber>{lineNumber}</LineNumber>
                            {/* Add empty-line class if content is effectively empty after parsing */}
                            <LineContent className={!lineContent.trim() && !String(parsedContent).includes('diff-placeholder') ? 'empty-line' : ''}>
                                {/* Render parsed content or non-breaking space if empty */}
                                {lineContent.trim() || String(parsedContent).includes('diff-placeholder') ? parsedContent : <>&nbsp;</>}
                            </LineContent>
                        </LineContainer>
                    );
                })}
            </SideBySideDocument>
        );

        return (
            <SideBySideView>
                {renderDocumentLines(lines1, doc1Ref, handleScroll1)}
                {renderDocumentLines(lines2, doc2Ref, handleScroll2)}
            </SideBySideView>
        );
    };

    const renderSummaryView = () => {
        if (!comparisonResult || !comparisonResult.summary) return <p>Summary data not available.</p>;
        const { summary, llmSummary } = comparisonResult;

        return (
            <>
                <SummaryStats>
                    <StatCard>
                        <StatValue>{summary.changes !== undefined ? summary.changes : 'N/A'}</StatValue>
                        <StatLabel>Changes</StatLabel>
                    </StatCard>
                    <StatCard>
                        <StatValue>{summary.additions !== undefined ? summary.additions : 'N/A'}</StatValue>
                        <StatLabel>Additions</StatLabel>
                    </StatCard>
                    <StatCard>
                        <StatValue>{summary.deletions !== undefined ? summary.deletions : 'N/A'}</StatValue>
                        <StatLabel>Deletions</StatLabel>
                    </StatCard>
                    <StatCard>
                        <StatValue>{summary.similarity !== undefined ? `${summary.similarity}%` : 'N/A'}</StatValue>
                        {/* Wrap label and icon for alignment */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                            <StatLabel style={{ margin: 0 }}>Similarity</StatLabel> {/* Remove default margin if any */}
                            <Tooltip title={jaccardExplanation} arrow placement="top">
                                {/* IconButton makes the icon clickable for accessibility */}
                                <IconButton size="small" style={{ padding: 0 }}>
                                    <InfoOutlined fontSize="inherit" style={{ color: 'rgba(0, 0, 0, 0.5)', verticalAlign: 'middle' }} />
                                </IconButton>
                            </Tooltip>
                        </div>
                    </StatCard>
                </SummaryStats>

                {summary.keyDifferences && summary.keyDifferences.length > 0 && (
                    <div>
                        <h3>Key Differences:</h3>
                        <ul>
                            {llmSummary.summaryLines.map((diff, index) => (
                                <li key={index} dangerouslySetInnerHTML={{ __html: String(diff).replace(/\n/g, '<br />') }} />
                            ))}
                        </ul>
                    </div>
                )}
            </>
        );
    };

    const FileInput = ({ id, label, selectedFile, onChange }) => (
        <FileInputContainer>
            <h3>{label}</h3>
            <p>Select the document file</p>
            <Button
                component="label"
                variant="outlined"
                startIcon={<UploadFile />}
                fullWidth
                style={{ marginBottom: '10px' }}
            >
                Upload File
                <input
                    id={id}
                    type="file"
                    hidden
                    accept=".doc,.docx,.pdf,.md,.txt,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf,text/markdown,text/plain"
                    onChange={onChange}
                />
            </Button>
            {selectedFile && (
                <FileNameDisplay>Selected: {selectedFile.name}</FileNameDisplay>
            )}
        </FileInputContainer>
    );

    return (
        <ComparisonSection>
            <DocumentSelectionArea>
                <FileInput
                    id="file-input-1"
                    label="Document 1 (Original)"
                    selectedFile={file1}
                    onChange={handleFile1Change}
                />
                <FileInput
                    id="file-input-2"
                    label="Document 2 (Modified)"
                    selectedFile={file2}
                    onChange={handleFile2Change}
                />
            </DocumentSelectionArea>

            {error && <p style={{ color: 'red', textAlign: 'center' }}>{error}</p>}

            <ButtonGroup>
                <StyledButton
                    variant="contained"
                    startIcon={<Compare />}
                    onClick={handleCompare}
                    disabled={!file1 || !file2 || isLoading}
                >
                    {isLoading ? 'Comparing...' : 'Compare Documents'}
                </StyledButton>
            </ButtonGroup>

            {comparisonMade && comparisonResult && !isLoading && (
                <ComparisonResults>
                    <ComparisonTabs>
                        <ComparisonTab
                            isActive={activeTab === 'inline'}
                            onClick={() => setActiveTab('inline')}
                        >
                            Inline View
                        </ComparisonTab>
                        <ComparisonTab
                            isActive={activeTab === 'side-by-side'}
                            onClick={() => setActiveTab('side-by-side')}
                        >
                            Side by Side
                        </ComparisonTab>
                        <ComparisonTab
                            isActive={activeTab === 'summary'}
                            onClick={() => setActiveTab('summary')}
                        >
                            Summary
                        </ComparisonTab>
                    </ComparisonTabs>

                    {activeTab === 'inline' && renderInlineDiffView()}
                    {activeTab === 'side-by-side' && renderSideBySideView()}
                    {activeTab === 'summary' && renderSummaryView()}
                </ComparisonResults>
            )}
            {isLoading && <p style={{ textAlign: 'center' }}>Loading comparison...</p>}
        </ComparisonSection>
    )
}
